import { FlightSeatMapDetails } from "@/types/flights/flight-seat-map"

import { Timestamp } from "../date"
import { Nullable, Optional } from "../util"
import { FlightItinerary } from "./flight-itinerary"

export type FlightBookingStatus =
  | "not-started"
  | "in-progress"
  | "submitting"
  | "pending-confirm"
  | "ticketing-error"
  | "partial-cancelled"
  | "error"
  | "expired"
  | "confirmed"
  | "cancelled"
  | "pending-cancel"

export const PRE_SUBMIT_STATUSES: FlightBookingStatus[] = [
  "not-started",
  "in-progress",
  "submitting",
]

export const FlightBookingStatuses: FlightBookingStatus[] = [
  "not-started",
  "in-progress",
  "submitting",
  "pending-confirm",
  "ticketing-error",
  "partial-cancelled",
  "error",
  "expired",
  "confirmed",
  "cancelled",
  "pending-cancel",
]

export const FlightSeatPreferenceMap = {
  aisle: "Aisle",
  window: "Window",
  adjacent: "Adjacent",
} as const

export type FlightSeatPreference = keyof typeof FlightSeatPreferenceMap

export interface FlightTraveler {
  first_name: string
  last_name: string
  middle_name: string
  date_of_birth: string
  gender: "M" | "F"
  email?: Optional<string>
  phone_number?: Optional<string>
  lap_infant?: Optional<boolean>
  seat_preference?: Optional<"aisle" | "window" | "adjacent">
  meal_preference?: Optional<string>
  redress_number?: Optional<string>
  known_traveler_number?: Optional<string>
  frequent_flyer_numbers?: Optional<
    {
      airline_iata: string
      value: string
    }[]
  >
  flight_seats?: Optional<
    {
      flight_number: number
      number: string
      label: string
    }[]
  >
  ticket_type?: string
  ticket_numbers?: string[]
  ticket_details?: unknown[]
}

export interface FlightCreditCardInfo {
  card_type: string
  card_number: string
  name_on_card: string
  expiration_month: string
  expiration_year: string
  cvv_code: string
}

export interface FlightBilling {
  street1: string
  street2: string
  email: string
  city: string
  state: string
  zip_code: string
  phone_number: string
  country_code: string
}

export interface FlightBookingProperties {
  flight_price_watch_quote_ref?: string
  travelers?: FlightTraveler[]
  billing?: FlightBilling
  customer_service_phone?: string
  is_split_ticket?: boolean
  agent_pnr_locator?: string
  airline_confirmation_number?: string
  post_book_seat_selection?: boolean
  submitted_at?: number
  seat_map_details?: FlightSeatMapDetails[]
  void_window_close?: Optional<number>
}

type PricelineBookingStatus =
  | "CONSOLIDATOR"
  | "PENDING"
  | "ACCEPTED"
  | "FAILED"
  | "PENDING_CANCEL"
  | "VOIDED"
  | "MIXED"

export interface FlightBooking {
  id: Nullable<number>
  status: FlightBookingStatus
  vendor: "priceline" | "sabre"
  flight_itinerary?: Optional<FlightItinerary>
  properties: FlightBookingProperties
  vendor_data: {
    bundle_id?: Optional<string>
    booking_id?: Optional<string>
    airline_confirmation_number?: Optional<string>
    booking_status?: Optional<PricelineBookingStatus>
    split_ticket_booking?: {
      booking_id: string
      slice_id: number
      booking_status: string
      booking_status_reason: string
    }[]
    latest_lookup_json: unknown
  }
  sandbox: boolean
  created_at: Nullable<Timestamp>
  updated_at: Nullable<Timestamp>
}

export interface PCIVaultCaptureEndpoint {
  url_path: string
  unique_id: string
  expires_at: Timestamp
  secret: string
}

export interface PCIVaultCaptureResponse {
  reference: string
  token: string
  user: string
  stored_at: string
}

export type FlightBookingMode = "sandbox" | "prod"
